import api from "api";
import { supportApiBase } from "api/constants";
import { useMutation, useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";
import { ALL_LOCALES } from "../views/lessons/constants";
import { LessonFormData, LocalizedQuestion, PartFormData } from "../views/lessons/types";
import { readLocale } from "../views/lessons/utils";
import { LessonDto, PartDto, PartTranslationDto } from "./lessonDtos";

type TranslationBody = {
  locale: string;
  title: string;
  // Omit image_url entirely when no image is uploaded. Sending the field for
  // empty/null values can cause the backend to create a translation row with
  // a meaningless image_url value — only include it when we actually have a URL.
  image_url?: string;
  content: Array<{ type: PartFormData["content"][number]["type"]; content: string }>;
  questions: Array<{
    title: string;
    tip_description: string;
    answers: Array<{ text: string; correct: boolean }>;
  }>;
};

const buildTranslationBody = (part: PartFormData, locale: string): TranslationBody => {
  const imageUrl = readLocale(part.imageUrl, locale);
  const body: TranslationBody = {
    locale,
    title: readLocale(part.title, locale),
    // Always send the full block list — even when a locale has no content for a
    // block — so that every locale ends up with the same array length. Dropping
    // empty blocks would misalign indexes when mergeLessonLocales() merges by
    // position on reload.
    content: (part.content ?? []).map(b => ({ type: b.type, content: readLocale(b.content, locale) })),
    questions: (part.questions ?? []).map(q => ({
      title: readLocale(q.title, locale),
      tip_description: readLocale(q.tipDescription, locale),
      answers: (q.answers ?? []).map(a => ({
        text: readLocale(a.text, locale),
        correct: !!a.correct,
      })),
    })),
  };
  if (imageUrl) body.image_url = imageUrl;
  return body;
};

// The backend requires a single correct_alternative index per question at the
// part level, but the actual source of truth for which answers are correct is
// translation.answers[].correct (which supports multiple). We send the first
// correct index to satisfy the schema.
const deriveCorrectAlternative = (question: LocalizedQuestion): number => {
  const idx = (question.answers ?? []).findIndex(a => a.correct);
  return idx < 0 ? 0 : idx;
};

export type SaveLessonResult = { id: number };

export const useSaveLesson = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (form: LessonFormData): Promise<SaveLessonResult> => {
      // 1. Lesson-level create/update
      let lessonId: number;
      if (form.id) {
        // @ts-ignore — v2 endpoint, not in OpenAPI types
        await api(`PUT /lessons/${form.id}`, {
          root: supportApiBase,
          data: { target_joints: form.target_joints },
        });
        lessonId = form.id;
      } else {
        // @ts-ignore — v2 endpoint, not in OpenAPI types
        const lesson: LessonDto = await api.parse(`POST /lesson`, {
          root: supportApiBase,
          data: { target_joints: form.target_joints },
        });
        lessonId = lesson.id;
      }

      // 2. Parts must be created/updated sequentially to preserve ordering
      //    semantics on the backend, but per-locale translations within a part
      //    are independent and can fan out in parallel.
      const parts = form.parts;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const questions = part.questions ?? [];
        const partBody = {
          order: i + 1, // BE expects 1-based index
          questions: questions.map((q, qi) => ({
            order: qi + 1, // BE expects 1-based index
            correct_alternative: deriveCorrectAlternative(q),
          })),
        };

        let partId: number;
        if (part.id) {
          // @ts-ignore — v2 endpoint, not in OpenAPI types
          await api(`PUT /lessons/${lessonId}/part/${part.id}`, {
            root: supportApiBase,
            data: partBody,
          });
          partId = part.id;
        } else {
          // @ts-ignore — v2 endpoint, not in OpenAPI types
          const savedPart: PartDto = await api.parse(`POST /lessons/${lessonId}/part`, {
            root: supportApiBase,
            data: partBody,
          });
          partId = savedPart.id!;
          part.id = partId;
        }

        // 3. Fan out translation saves for every locale with content.
        //    Build the body first, then skip locales with no meaningful content.
        const created = await Promise.all(
          ALL_LOCALES.map(async locale => {
            const body = buildTranslationBody(part, locale);
            const hasContent =
              body.title.trim() ||
              body.image_url ||
              body.content.some(b => b.content.trim()) ||
              body.questions.some(q => q.title.trim() || q.answers.some(a => a.text.trim()));
            if (!hasContent) return null;
            const existingId = part.translationIds?.[locale];
            if (existingId) {
              // @ts-ignore — v2 endpoint, not in OpenAPI types
              await api(`PUT /lessons/${lessonId}/part/${partId}/translation/${existingId}`, {
                root: supportApiBase,
                data: body,
              });
              return null;
            }
            const createPath = `POST /lessons/${lessonId}/part/${partId}/translation`;
            // @ts-ignore — v2 endpoint, not in OpenAPI types
            const res: PartTranslationDto = await api.parse(createPath, { root: supportApiBase, data: body });
            return [locale, res.id!] as const;
          })
        );
        for (const entry of created) {
          if (entry) part.translationIds = { ...part.translationIds, [entry[0]]: entry[1] };
        }
      }

      return { id: lessonId };
    },
    {
      // Awaiting the detail invalidation makes mutateAsync resolve only after
      // the lesson query has refetched, so callers can remount the editor
      // against the fresh server state without a visible stale-data flash.
      onSuccess: async ({ id }) => {
        queryClient.invalidateQueries(queryKeys.lessons);
        await queryClient.invalidateQueries(queryKeys.lesson(id));
      },
    }
  );
};
